import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Control, ControlOptions, DomEvent, DomUtil } from 'leaflet'
import { createPortal } from 'react-dom'
import { TileLayer, useMap } from 'react-leaflet'
import tw from 'tailwind-styled-components'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { Card } from '../../Card'
import MapIcon from '@foros-fe/assets/icons/map.svg'
import BaseTile from '@foros-fe/assets/images/base-tile.png'
import OrthographicTile from '@foros-fe/assets/images/orthographic-tile.png'
import TopographicTile from '@foros-fe/assets/images/topographic-tile.png'

type BaseTileLayerProps = {
  url: string
  image: StaticImageData
  title: string
  selected: boolean
  onClick(url: string): void
}

const controlContainer = DomUtil.create('div')
const LeafletTileLayerControl = Control.extend({
  onAdd() {
    DomEvent.disableClickPropagation(controlContainer)

    return controlContainer
  },
})
export const tileURLs = {
  base: `https://api.mapbox.com/styles/v1/danieliusforos/ckwusk8l345d615nvts439he1/tiles/256/{z}/{x}/{y}@2x?access_token=${process.env.NEXT_PUBLIC_MAPBOX_API_KEY}`,
  orthographic: `https://api.mapbox.com/styles/v1/danieliusforos/ckraqq3xk2mkq18ou9lqae6r9/tiles/256/{z}/{x}/{y}@2x?access_token=${process.env.NEXT_PUBLIC_MAPBOX_API_KEY}`,
  topographic: `https://api.mapbox.com/styles/v1/danieliusforos/ckx5zungm7kd314o3pzmxpomq/tiles/256/{z}/{x}/{y}@2x?access_token=${process.env.NEXT_PUBLIC_MAPBOX_API_KEY}`,
}

const Popup = tw.div`
  absolute
  right-[50px]
  top-0
`
const TileLayerContainer = tw.div<{ $active: boolean }>`
  flex
  w-28
  h-27
  rounded-lg
  flex-col
  overflow-hidden
  font-medium
  cursor-pointer
  ${({ $active }) => ($active ? 'border-2 border-secondary-500' : 'border border-gray-400')}
`

const BaseTileLayer: FC<BaseTileLayerProps> = ({ url, image, title, onClick, selected }) => {
  const handleClick = useCallback(() => onClick(url), [onClick, url])

  return (
    <TileLayerContainer onClick={handleClick} $active={selected}>
      <Image src={image} />
      <span className='px-2 py-1 text-center'>{title}</span>
    </TileLayerContainer>
  )
}

export const TileLayerControl = ({ position = 'topright' }: ControlOptions) => {
  const { t } = useTranslation('common')
  const [popupOpen, setPopupOpen] = useState(false)
  const [tileURL, setTileURL] = useState(tileURLs.orthographic)
  const map = useMap()
  const closePopup = useCallback(() => setPopupOpen(false), [])
  const handleLayerChange = useCallback(
    (url: string) => {
      setTileURL(url)

      closePopup()
    },
    [closePopup]
  )
  const tileLayers = useMemo(
    () => [
      {
        url: tileURLs.orthographic,
        image: OrthographicTile,
        title: t('map.tiles.orthographic'),
      },
      {
        url: tileURLs.topographic,
        image: TopographicTile,
        title: t('map.tiles.topographic'),
      },
      {
        url: tileURLs.base,
        image: BaseTile,
        title: t('map.tiles.base'),
      },
    ],
    [t]
  )
  const togglePopup = useCallback(() => setPopupOpen((flag) => !flag), [])

  useEffect(() => {
    const control = new LeafletTileLayerControl({ position })
    map.addControl(control)

    map.on('exitFullscreen', closePopup)

    return () => {
      map.removeControl(control)
      map.off('exitFullscreen', closePopup)
    }
  }, [map, position, closePopup])

  return (
    <>
      <TileLayer zIndex={0} key={tileURL} url={tileURL} />
      {createPortal(
        <div className='leaflet-bar'>
          <a className='cursor-pointer' onClick={togglePopup}>
            <MapIcon width={19} height={18} />
          </a>
          {popupOpen ? (
            <Popup>
              <Card className='border border-gray-400'>
                <div className='flex flex-row gap-4'>
                  {tileLayers.map((layer) => (
                    <BaseTileLayer
                      key={layer.title}
                      {...layer}
                      onClick={handleLayerChange}
                      selected={layer.url === tileURL}
                    />
                  ))}
                </div>
              </Card>
            </Popup>
          ) : null}
        </div>,
        controlContainer
      )}
    </>
  )
}
